/**
 * @fileoverview naddr in, page out -- or one single "not available".
 *
 * TWO WAYS A PAGE ARRIVES, and which one is used is written in the address
 * itself, so nothing here has to guess:
 *
 *   a 30023  is a public markdown page and the relay already handed us the
 *            body. Rendered straight from the event, no blob, no network call
 *            after the relay.
 *   a 33301  is blob-backed -- raw HTML, or a private page. Its body is fetched
 *            from the servers it names and CHECKED AGAINST ITS HASH before a
 *            byte of it is shown.
 *
 * THIS FILE ALSO EXISTS TO COLLAPSE FAILURES, and that is a design requirement
 * rather than tidiness.
 *
 * A private page is encrypted: its blob is ciphertext and, by the operator's
 * 2026-09-15 ruling, so is its index event. A stranger who holds a private
 * page's address must not be able to tell it apart from an address that names
 * nothing at all -- otherwise the existence of the page, and the fact that this
 * particular person has one, leaks to anyone who can guess or is handed a link.
 *
 * So every way this can end badly returns the SAME value, with no reason
 * attached and nothing for a caller to switch on:
 *
 *   - the naddr is not a page address
 *   - no relay holds the event
 *   - the event is malformed
 *   - the event is ciphertext we hold no key for
 *   - every server refuses, times out, or 404s
 *   - the bytes come back and do not match the hash
 *
 * There is no `reason` field to forget to strip in the UI later, and no
 * exception type whose name reaches a console in production.
 *
 * A PRIVATE PAGE NOW OPENS, FOR THE PEOPLE ENTITLED TO IT, and it opens without
 * changing any of the above. The event's tags are sealed, so a reader with no
 * key gets `parseIndexEvent` returning null and lands on the same single
 * unavailable answer as a stranger with a bad address -- which is what it was
 * already doing before the key existed. A reader WITH the key opens the sealed
 * tag into the plaintext event it would have been and continues down the
 * ordinary blob path from there: fetched from the servers it names, CHECKED
 * AGAINST ITS HASH, and only then decrypted.
 *
 * THE VERIFY-THEN-DECRYPT ORDER IS THE POINT. The hash is of the ciphertext,
 * which is what a Blossom server actually holds, so bytes from a server we do
 * not trust are held against something the author signed before they reach the
 * crypto at all.
 *
 * WHERE THE KEY COMES FROM, in the order it is looked for:
 *
 *   1. the OWNER's envelope, which rides on the page's own event, so an author
 *      opens their own page with no further lookup;
 *   2. a SHARE event addressed to this reader, which costs a second relay
 *      query and is only attempted when the first fails.
 *
 * A READER WITH NO SIGNER SKIPS BOTH, which is the flagship case: somebody with
 * a link and no account, reading a public page, who must never be asked for a
 * key they do not need.
 *
 * RAW HTML NOW RENDERS. It used to stop here with a placeholder, because
 * showing somebody else's markup without a sandbox is how a page store becomes
 * an XSS host. The sandbox exists now, so the HTML branch fetches and verifies
 * like every other blob and the renderer decides what to do with it.
 *
 * TIMING IS PART OF IT. "No event anywhere" resolves in milliseconds and "four
 * servers timed out" takes seconds, which is a distinguisher that no amount of
 * identical wording hides. Every unavailable outcome is therefore held until a
 * fixed floor has elapsed. It is not a proof against a determined observer with
 * many samples, and it is not offered as one -- it removes the casual version,
 * where a page's existence is legible from how fast the error appeared. It is
 * also the piece that is genuinely hard to retrofit, because by then callers
 * have been written to render as soon as they know.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth/core';

import { readOwnerKey } from './collectionKeys';
import { parseIndexEvent, type PageIndex, type RawEvent } from './indexEvent';
import { PAGE_INDEX_KIND, RENDERABLE_PAGE_KINDS } from './kinds';
import { memberKeyFromCollections } from './memberKey';
import { decryptWithPageKey } from './pageKey';
import { isPrivatePageEvent, openSealedPage } from './privatePage';
import { type Share, sharesFor } from './shareCollection';

import { config } from '@/config/environment';
import { blobUrls, decodeUtf8, fetchVerifiedBlob } from '@/services/blossom/fetchBlob';
import { fetchAuthorServers } from '@/services/blossom/serverList';
import { decodeIdentifier } from '@/services/nostr/identifiers';
import { fetchNewest, relaysFor } from '@/services/nostr/relays';


export type PageLoad =
  | {
      status: 'ready';
      index: PageIndex;
      body: string;
      /**
       * The relays this page was looked for on: the address's own hints plus
       * our fallback.
       *
       * Carried out so that a follow-up fetch for the same page -- today its
       * theme -- asks the relays that already proved they know about it,
       * instead of re-deriving the set or defaulting to ours. A theme almost
       * always lives beside the pages that use it, and a renderer that only
       * ever asked our relay could not style anybody else's page, which is the
       * same property the address's relay hint exists to give the page itself.
       *
       * Only on the ready branch. An unavailable result says nothing about
       * where it looked, because saying so would be a detail a stranger could
       * use to tell one kind of failure from another.
       */
      relays: string[];
      /**
       * The key this page was sealed under. PRESENT ONLY when the page was
       * private AND this reader could open it.
       *
       * Carried out because the EDITOR cannot do without it: saving an edit of a
       * private page under a fresh key would silently revoke everyone it was
       * shared with, so the publisher refuses an edit that arrives without one.
       * Recovering it a second time would cost another relay query and another
       * signer prompt for a value this function was holding a moment ago.
       *
       * ITS PRESENCE IS ALSO HOW A CALLER KNOWS THE PAGE IS PRIVATE, which is
       * why there is no separate boolean. A flag beside the key would be a
       * second thing to keep in step, and the case they could disagree about --
       * a private page with no key -- is not a `ready` result at all.
       *
       * NOTHING ON THE READING SCREEN NEEDS IT, and nothing there uses it.
       */
      pageKey?: ContentKey;
    }
  | { status: 'unavailable' };

/** See the timing note above. */
const UNAVAILABLE_FLOOR_MS = 900;

const UNAVAILABLE: PageLoad = { status: 'unavailable' };

/**
 * Which step a load failed at, for a caller that asks.
 *
 * THE READING SCREEN NEVER ASKS, and its answer stays one value for every
 * reason, for the reason this file opens with. The command line asks, because
 * a recipient told only "not available" cannot tell a share that never reached
 * them from a relay that did not answer, and neither can whoever they report it
 * to. The caller holding the trace already holds the address and the key, so
 * nothing here tells them anything a stranger could use.
 */
export type LoadStep =
  | { step: 'address' }
  | { step: 'not-found' }
  | { step: 'wrong-author' }
  | { step: 'no-key'; sharesToThisKey: number; unreadableShares: number }
  | { step: 'sealed' }
  | { step: 'malformed' }
  | { step: 'address-mismatch' }
  | { step: 'blob'; servers: string[] }
  | { step: 'decrypt' };

export async function loadPage(
  naddr: string,
  options: { signer?: SignerInterface; now?: () => number; trace?: (step: LoadStep) => void } = {}
): Promise<PageLoad> {
  const now = options.now ?? (() => Date.now());
  const startedAt = now();
  const result = await attempt(naddr, options.signer, options.trace ?? (() => {}));
  if (result.status === 'ready') return result;

  const elapsed = now() - startedAt;
  if (elapsed < UNAVAILABLE_FLOOR_MS) {
    await new Promise((resolve) => setTimeout(resolve, UNAVAILABLE_FLOOR_MS - elapsed));
  }
  return UNAVAILABLE;
}

async function attempt(
  naddr: string,
  signer: SignerInterface | undefined,
  trace: (step: LoadStep) => void
): Promise<PageLoad> {
  const fail = (step: LoadStep): PageLoad => {
    trace(step);
    return UNAVAILABLE;
  };
  let decoded;
  try {
    decoded = decodeIdentifier(naddr);
  } catch {
    // decodeIdentifier throws on an nsec. Someone has put a private key in a
    // URL. Nothing is rendered and nothing is echoed back.
    return fail({ step: 'address' });
  }

  // An `naddr` for some other addressable kind is a valid Nostr reference to
  // something that is not a page. Treated as unavailable rather than as a
  // different error, for the same reason everything else here is.
  if (!decoded || decoded.type !== 'address' || !RENDERABLE_PAGE_KINDS.includes(decoded.kind)) {
    return fail({ step: 'address' });
  }

  const relays = relaysFor(decoded.relays);
  // Filtered to the ONE kind the address names, not to both. The address is
  // authoritative about which kind the page is, and querying both would let a
  // 30023 that happens to share an identifier answer for a blob-backed page.
  const event = await fetchNewest(relays, {
    kinds: [decoded.kind],
    authors: [decoded.pubkey],
    '#d': [decoded.identifier],
  });
  if (!event) return fail({ step: 'not-found' });

  // CHECKED BEFORE ANY KEY WORK, and this ordering is not only an optimisation.
  // The address asked for is authoritative (see the long note below), and a
  // relay that answered with somebody else's event must not get as far as
  // costing this reader a signer round trip -- on a remote signer, a prompt on
  // their phone -- for a page they never asked for.
  if (event.pubkey !== decoded.pubkey) return fail({ step: 'wrong-author' });

  // A PRIVATE PAGE'S TAGS ARE SEALED, so there is nothing for the parser to read
  // until the key opens them. Without a key this falls straight through with
  // `plain` still holding the sealed event, `parseIndexEvent` returns null, and
  // it lands exactly where a malformed event lands -- which is the intent, and
  // is what it already did before any of this existed.
  let plain: RawEvent = event;
  let pageKey: ContentKey | undefined;
  if (isPrivatePageEvent(event)) {
    const found = signer
      ? await keyForPage(event, decoded.pubkey, decoded.identifier, signer, relays)
      : { key: undefined, sharesToThisKey: 0, unreadableShares: 0 };
    pageKey = found.key;
    if (!pageKey) {
      return fail({
        step: 'no-key',
        sharesToThisKey: found.sharesToThisKey,
        unreadableShares: found.unreadableShares,
      });
    }
    const opened = openSealedPage(event, pageKey);
    if (!opened) return fail({ step: 'sealed' });
    plain = opened;
  }

  const index = parseIndexEvent(plain);
  // A private page nobody here holds a key for lands here, in the same place a
  // malformed event lands, which is the intent.
  if (!index) return fail({ step: 'malformed' });

  // THE ADDRESS IN THE URL IS AUTHORITATIVE, NOT THE RELAY'S ANSWER.
  //
  // The filter above asks for one author's page and a relay replies with
  // whatever it likes; the signature it carries proves only that SOMEBODY
  // signed it. The relay list here is not ours either -- an `naddr` carries its
  // own hints, and those were chosen by whoever produced the link. So "here is
  // Alice's page, naddr1..." can name a relay the sharer runs, and without this
  // that relay could answer with its own page and have it render under Alice's
  // address, with her name on the byline.
  //
  // Checked against the address rather than trusted, for the same reason the
  // blob below is checked against its hash: what we asked for is named in the
  // request, so the answer can be held to it.
  if (
    index.address.pubkey !== decoded.pubkey ||
    index.address.identifier !== decoded.identifier ||
    index.address.kind !== decoded.kind
  ) {
    return fail({ step: 'address-mismatch' });
  }

  // A public markdown page. The relay gave us the body and the event signature
  // already covers it, so there is nothing left to fetch and nothing left to
  // verify -- the hash check below exists because blob bytes come from a server
  // we do not trust, and here they did not.
  if (index.source.kind === 'inline') {
    return { status: 'ready', index, body: index.source.markdown, relays };
  }

  // RAW HTML TAKES THE SAME PATH AS EVERYTHING ELSE HERE, which is the point.
  // It is fetched from the servers the event names and checked against the
  // event's hash before a byte of it is returned, exactly as a markdown blob
  // is. What differs is only what the RENDERER does with it: markdown is
  // sanitised into our own document, HTML is given a sandboxed document of its
  // own. Neither of those is this function's business, and treating HTML as a
  // special case here would have meant a second fetch path to keep honest.
  const bytes = await fetchBody(index.source, index.address.pubkey, relays);
  if (!bytes) return fail({ step: 'blob', servers: [...index.source.serverHints] });

  const text = decodeUtf8(bytes);
  if (!pageKey) return { status: 'ready', index, body: text, relays };

  // From here the page is private and this reader holds its key.

  // VERIFIED FIRST, DECRYPTED SECOND. `fetchBody` has already held these bytes
  // against the hash the author signed, so what reaches the crypto here is the
  // author's ciphertext rather than whatever a server felt like sending. A
  // failure at this point is a page that verified and still will not open,
  // which is not a case a reader can act on and is therefore not one they are
  // told apart from any other.
  try {
    return {
      status: 'ready',
      index,
      body: decryptWithPageKey(text, pageKey),
      relays,
      pageKey,
    };
  } catch {
    return fail({ step: 'decrypt' });
  }
}

/**
 * The key for a private page, from an ORDERED LIST OF SOURCES.
 *
 *   1. the reader's own owner envelope, on the page's own event
 *   2. a share event naming this page
 *   3. any collection this reader holds a key for whose sealed member list
 *      names this page
 *
 * THE ORDER IS THE WHOLE OPTIMISATION. The common case is an author opening
 * their own page, and that case must not touch a relay at all. Each later source
 * costs more and is tried only when the cheaper ones have nothing.
 *
 * SOURCE 3 IS WHAT MAKES SHARING A SITE MEAN ANYTHING, and its absence was a
 * false success rather than a missing feature: a recipient of a shared
 * collection saw every private member's title in its real position and could not
 * open one of them, while the author saw a perfect site because they open every
 * page from their own envelope. Ruled by the operator via
 * `cloistr-orchestrator` 2026-09-17; `memberKey.ts` carries the reasoning for
 * why it is a source rather than something passed down from the route.
 *
 * SOURCES 2 AND 3 SHARE ONE FETCH. Both read the list of things shared with this
 * reader, and asking the relay for it twice would double the cost of the
 * uncommon path to buy nothing.
 *
 * NOTHING HERE THROWS. A key tag that is present and does not open means this
 * reader is not the owner, which is the ordinary case for a recipient rather
 * than an error; a lookup that fails leaves the reader exactly where a stranger
 * is, which is a real answer. Every one of them falls through to undefined, and
 * undefined lands on the same unavailable page as everything else.
 */
async function keyForPage(
  event: RawEvent,
  pubkey: string,
  identifier: string,
  signer: SignerInterface,
  relays: string[]
): Promise<{ key: ContentKey | undefined; sharesToThisKey: number; unreadableShares: number }> {
  const coordinate = `${PAGE_INDEX_KIND}:${pubkey}:${identifier}`;
  const none = { key: undefined, sharesToThisKey: 0, unreadableShares: 0 };

  try {
    const owned = await readOwnerKey(event.tags, coordinate, signer);
    if (owned) return { ...none, key: owned };
  } catch {
    // Not the owner. Fall through to what was shared with them.
  }

  let shares: readonly Share[] = [];
  let unreadable = 0;
  try {
    const found = await sharesFor(signer, relays);
    shares = found.shares;
    unreadable = found.unreadable;
  } catch {
    return none;
  }
  const counted = { sharesToThisKey: shares.length, unreadableShares: unreadable };

  // Newest wins if the same page was shared twice, which happens when a key is
  // rotated and re-issued.
  const direct = shares.filter((s) => s.address === coordinate).pop();
  if (direct) return { ...counted, key: direct.key };

  try {
    const viaSite = await memberKeyFromCollections(coordinate, signer, relays, shares);
    return { ...counted, key: viaSite ?? undefined };
  } catch {
    return { ...counted, key: undefined };
  }
}

/**
 * The page's own hints first; the author's BUD-03 list only if those fail.
 *
 * Splitting it this way keeps the second relay round trip off the happy path.
 * Most pages are served by the first server they name.
 */
async function fetchBody(
  source: Extract<PageIndex['source'], { kind: 'blob' }>,
  pubkey: string,
  relays: string[]
): Promise<ArrayBuffer | null> {
  const fromHints = await fetchVerifiedBlob(
    source.hash,
    blobUrls(source.hash, {
      pageHints: source.serverHints,
      authorServers: [],
      fallbacks: config.fallbackBlossomServers,
    }),
    { timeoutMs: config.blobTimeoutMs }
  );
  if (fromHints) return fromHints;

  const authorServers = await fetchAuthorServers(pubkey, relays);
  if (authorServers.length === 0) return null;

  return fetchVerifiedBlob(
    source.hash,
    blobUrls(source.hash, {
      pageHints: [],
      authorServers,
      fallbacks: [],
    }),
    { timeoutMs: config.blobTimeoutMs }
  );
}
